import { Autocomplete, CircularProgress, ListItem, ListItemText } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce, noop } from 'lodash';
import PropTypes from 'prop-types';
import { useForm } from 'react-final-form';

import { listSigsApi } from 'api/prescription';

import { SigLengthLimits } from 'enums/SigManagement.enum';
import TextField from './TextField';
import { validateMaxLength, validateMinLength } from './formValidations';

const SigsAutoSuggest = ({
  name,
  id,
  label,
  autoFocus,
  maxLength,
  handleSelect,
  isPrescriptionReview,
}) => {
  const form = useForm();
  const [sigs, setSigs] = useState([]);
  const [inputValue, setInputValue] = useState(form.getState().values[name] || '');
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const lastChangeReason = useRef('input');

  const onInputChange = (_, value, reason) => {
    lastChangeReason.current = reason;
    setInputValue(value);
    handleSelect({});
    if (reason === 'input') {
      setSelectedOption(null);
    }
  };

  const onChange = (_, sig) =>
    form.batch(() => {
      setSelectedOption(sig);
      handleSelect(sig);
      form.change(name, sig);
    });

  const validateSelectionRequired = (value) => {
    if (isPrescriptionReview && (!value || !value?.sig)) {
      return 'Please select a sig from the list';
    }
    return undefined;
  };

  const debouncedSearch = useMemo(() => {
    const searchFunc = (sig) => {
      setLoading(true);
      listSigsApi({ searchText: sig })
        .then((fetchedSigs) => setSigs(fetchedSigs))
        .catch(noop)
        .finally(() => setLoading(false));
    };

    return debounce(searchFunc, 500, { trailing: true, leading: false });
  }, []);

  useEffect(() => {
    if (lastChangeReason.current === 'reset') {
      return;
    }
    setSigs([]);
    if (inputValue.length >= 3) {
      setLoading(true);
      debouncedSearch(inputValue);
    }
  }, [inputValue, debouncedSearch]);

  useEffect(() => {
    if (sigs.length && inputValue.length < 3) {
      setSigs([]);
    }
  }, [inputValue, sigs]);

  const noResults = !loading && inputValue.length >= 3 && sigs.length === 0;
  const displayOptions = noResults ? [{ sig: 'No data found', noData: true }] : sigs;

  return (
    <Autocomplete
      freeSolo
      id={`${id}_-autosuggest`}
      options={displayOptions}
      getOptionDisabled={(option) => Boolean(option.noData)}
      getOptionLabel={(sig) => sig.sig || inputValue}
      renderOption={(props, { sig, sigId }) => (
        <ListItem
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          key={sigId || 'no-data'}
          aria-selected={!!sigId && sigId === selectedOption?.sigId}
        >
          <ListItemText primary={sig} />
        </ListItem>
      )}
      filterOptions={(options) => options}
      disableClearable
      value={inputValue || ''}
      onInputChange={onInputChange}
      onChange={onChange}
      renderInput={(params) => {
        return (
          <TextField
            name={name}
            id={id}
            label={label}
            required
            params={params}
            endAdornment={loading ? <CircularProgress size={20} /> : undefined}
            autoFocus={autoFocus}
            maxLength={maxLength}
            validations={[
              validateMaxLength(
                `Maximum of ${SigLengthLimits.SIG_MAX} characters`,
                SigLengthLimits.SIG_MAX
              ),
              validateMinLength(
                `Minimum of ${SigLengthLimits.SIG_MIN} characters`,
                SigLengthLimits.SIG_MIN
              ),
              validateSelectionRequired,
            ]}
          />
        );
      }}
    />
  );
};

SigsAutoSuggest.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  autoFocus: PropTypes.bool,
  maxLength: PropTypes.number,
  handleSelect: PropTypes.func,
  isPrescriptionReview: PropTypes.bool,
};

SigsAutoSuggest.defaultProps = {
  autoFocus: false,
  maxLength: 100,
  handleSelect: () => {},
  isPrescriptionReview: false,
};

export default SigsAutoSuggest;
